import React, { useEffect, useCallback, useLayoutEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import {
  DataGridPro as MuiDataGrid,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import DataGridToolbar from './DataGridToolbar/DataGridToolbar';

import * as styles from './DataGrid.scss';

// Options for auto-sizing column widths
const defaultAutoSizeOptions = {
  includeHeaders: true,
  includeOutliers: true,
  expand: true,
};

const DataGrid = (props) => {
  const apiRef = useGridApiRef();

  const {
    id,
    autoSizeOptions,
    checkboxSelection,
    columns,
    columnVisibilityModel,
    defaultInitialState,
    density,
    disableRowSelectionOnClick,
    enableColumnsButton,
    enableDensityButton,
    enableExportButton,
    enableFilterButton,
    enableRefreshButton,
    filterModel,
    getRowId,
    isLoading,
    keepNonExistentRowsSelected,
    localeText,
    noRowsOverlay,
    onColumnVisibilityModelChange,
    onFilterModelChange,
    onRowClick,
    refreshFn,
    rows,
    rowSelectionModel,
    onRowSelectionModelChange,
    showQuickFilter,
    slotProps,
    toolbarActionButtons,
  } = props;

  const showToolbar = Boolean(
    toolbarActionButtons
    || showQuickFilter
    || enableFilterButton
    || enableColumnsButton
    || enableDensityButton
    || enableExportButton
    || enableRefreshButton
  );

  // Restore grid state from sessionStorage (keyed by id) or use defaultInitialState.
  // Lazy initializer runs once on mount to avoid setState-in-effect.
  const [initialState] = useState(() => {
    // If there's no id or sessionStorage is unavailable, fall back immediately.
    if (!id || typeof sessionStorage === 'undefined') {
      return defaultInitialState ?? {};
    }

    const storageKey = `Bridg::${id}::dataGridState`;
    const stateFromStorage = sessionStorage.getItem(storageKey);

    if (!stateFromStorage) {
      return defaultInitialState ?? {};
    }

    try {
      return JSON.parse(stateFromStorage);
    } catch (e) {
      // Malformed/corrupted JSON: clear the bad value and fall back to defaultInitialState.
      console.error('Error parsing data grid state from session storage:', e);
      sessionStorage.removeItem(storageKey);
      return defaultInitialState ?? {};
    }
  });

  // Takes a snapshot of the DataGrid state, including filters, sorting, column visibility, etc.
  const snapshotState = React.useCallback(() => {
    if (id && apiRef?.current?.exportState && sessionStorage) {
      const currentState = apiRef.current.exportState();
      sessionStorage.setItem(`Bridg::${id}::dataGridState`, JSON.stringify(currentState));
    }
  }, [apiRef, id]);

  // Before window is unloaded, snapshot DataGrid state to session storage.
  useLayoutEffect(() => {
    window.addEventListener('beforeunload', snapshotState);

    return () => {
      window.removeEventListener('beforeunload', snapshotState);
      snapshotState();
    };
  }, [snapshotState]);

  // Auto-sizes columns to fit available width
  const autoSizeColumns = useCallback(() => {
    const autoSizeFn = apiRef.current?.autosizeColumns;
    if (autoSizeFn) autoSizeFn(autoSizeOptions ?? defaultAutoSizeOptions);
  }, [apiRef, autoSizeOptions]);

  // Resize columns anytime controlled column visibility model changes
  useEffect(() => {
    if (columnVisibilityModel) autoSizeColumns();
  }, [autoSizeColumns, columnVisibilityModel]);

  // Updates column visibility model and then auto-size columns
  const updateColumnVisibilityModel = useCallback((newModel) => {
    if (onColumnVisibilityModelChange) onColumnVisibilityModelChange(newModel);
    autoSizeColumns();
  }, [autoSizeColumns, onColumnVisibilityModelChange]);

  if (!initialState || !columns?.length) {
    return <CircularProgress />;
  }

  return (
    <div className={styles.brDataGrid}>
      <MuiDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        getRowId={getRowId}
        autosizeOnMount
        checkboxSelection={checkboxSelection}
        onRowClick={onRowClick}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        keepNonExistentRowsSelected={keepNonExistentRowsSelected}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        loading={isLoading}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={updateColumnVisibilityModel}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        onPaginationModelChange={autoSizeColumns}
        onResize={autoSizeColumns}
        resizeThrottleMs={333}
        ignoreDiacritics
        pagination
        autoPageSize
        hideFooterSelectedRowCount
        initialState={initialState}
        density={density}
        slots={{
          toolbar: DataGridToolbar,
          noRowsOverlay,
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
          toolbar: {
            showQuickFilter,
            quickFilterProps: {
              variant: 'outlined',
              debounceMs: 300,
            },
            enableFilterButton,
            enableColumnsButton,
            enableDensityButton,
            enableExportButton,
            enableRefreshButton,
            refreshFn,
            isRefreshing: isLoading,
            actionButtons: toolbarActionButtons,
          },
          basePagination: {
            showFirstButton: true,
            showLastButton: true,
          },
          ...slotProps,
        }}
        localeText={{
          toolbarColumns: '',
          toolbarDensity: '',
          toolbarExport: '',
          toolbarFilters: '',
          toolbarQuickFilterPlaceholder: 'Search...',
          ...localeText,
        }}
        sx={{
          // Don't add border around last clicked table cell
          '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          // Don't add border around last clicked header cell
          '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          borderColor: 'rgb(213, 229, 252)',
          '--DataGrid-rowBorderColor': 'rgb(213, 229, 252)',
        }}
        showToolbar={showToolbar}
      />
    </div>
  );
};

export default DataGrid;
